<template>
  <div>
    <div class="button-container">
      <div class="show-question-button-container">
        <fvl-button id="show-question-button" reference="show-question-button"
          :shortcutKeySequence="[keyMap.buttons.showQuestion.toLowerCase()]"
          :tooltip="`${showQuestionLabel} (${keyMap.buttons.showQuestion.toUpperCase()})`" :value="showQuestionLabel"
          :class="{ 'show-question-answer': true, 'finish-game': finishGame && questionDisplayMode !== 'Start' }"
          @click="showNextQuestionHandler()" :disabled="!['Start', 'Answer'].includes(questionDisplayMode) ||
            isSaving ||
            resumeGame || peekAnswer
            " />

        <fvl-button id="show-answer-button" reference="show-answer-button"
          :shortcutKeySequence="[keyMap.buttons.showAnswer.toLowerCase()]"
          :tooltip="`Show Answer (${keyMap.buttons.showAnswer.toUpperCase()})`" :value="'Show Answer'"
          class="show-question-answer" @click="showAnswerHandlerAction()"
          :disabled="questionDisplayMode !== 'ShowAnswer'" />
      </div>

      <div class="scorer-button-container">
        <div :class="{ c1: true, 'mimir-button': !isStakes }">
          <fvl-button id="pass-button" reference="pass-button"
            :shortcutKeySequence="[keyMap.buttons.pass.toLowerCase()]"
            :tooltip="`Pass (${keyMap.buttons.pass.toUpperCase()})`" :value="'Pass'" class="pass-button"
            @click="passHandler()" :disabled="questionDisplayMode !== 'Question'" />
        </div>
        <div class="c2" v-if="isStakes">
          <fvl-button id="stake-5-button" reference="stake-5-button" :tooltip="`Stake +5/-5 (free stake if direct)`"
            :value="'Stake 5'" :buttonType="stakeValue === 5 ? 'staked' : ''" @click="stakeHandler(5)"
            :disabled="questionDisplayMode !== 'Question'" />
          <fvl-button id="stake-10-button" reference="stake-10-button" :tooltip="`Stake +10/-10`" :value="'Stake 10'"
            :buttonType="stakeValue === 10 ? 'staked' : ''" @click="stakeHandler(10)"
            :disabled="questionDisplayMode !== 'Question'" />
          <fvl-button id="stake-20-button" reference="stake-20-button" :tooltip="`Stake +20/-20`" :value="'Stake 20'"
            :buttonType="stakeValue === 20 ? 'staked' : ''" @click="stakeHandler(20)"
            :disabled="questionDisplayMode !== 'Question'" />
        </div>
        <div :class="{ c3: true, 'mimir-button': !isStakes }">
          <fvl-button id="correct-button" reference="correct-button"
            :shortcutKeySequence="[keyMap.buttons.correct.toLowerCase()]"
            :tooltip="`Correct (${keyMap.buttons.correct.toUpperCase()})`" :icon="'fa-solid fa-check'"
            :class="{ 'mimir-button': !isStakes }" @click="correctHandler()" :buttonType="isStakes ? 'correct-button-stakes' : 'correct-button-mimir'
              " :disabled="(isStakes && stakeValue === 0) ||
                (!isStakes && questionDisplayMode !== 'Question')
                " />

          <fvl-button id="wrong-button" reference="wrong-button"
            :shortcutKeySequence="[keyMap.buttons.wrong.toLowerCase()]"
            :tooltip="`Wrong (${keyMap.buttons.wrong.toUpperCase()})`" :icon="'fa-solid fa-xmark'"
            :class="{ 'mimir-button': !isStakes }" :buttonType="isStakes ? 'wrong-button-stakes' : 'wrong-button-mimir'
              " @click="wrongHandler()" :disabled="(isStakes && stakeValue === 0) ||
                (!isStakes && questionDisplayMode !== 'Question')
                " />
        </div>
      </div>
      <div class="kill-undo-container">
        <fvl-button id="undo-button" reference="undo-button" :shortcutKeySequence="[keyMap.buttons.undo.toLowerCase()]"
          :tooltip="`Undo (${keyMap.buttons.undo.toUpperCase()})`" :icon="'fa-solid fa-rotate-left'"
          :class="{ 'undo-button': true }" @click="undoHandler()"
          :disabled="gamePlayHistory.length === 0 || peekAnswer" />

        <fvl-button id="kill-button" reference="kill-button" :shortcutKeySequence="[keyMap.buttons.kill.toLowerCase()]"
          :tooltip="`Kill (${keyMap.buttons.kill.toUpperCase()})`" :icon="'fa-solid fa-bolt'"
          :class="{ 'kill-button': true }" @click="killHandler()" :disabled="questionDisplayMode !== 'Question'" />

        <fvl-button id="reload-button" reference="reload-button" :tooltip="`Reload question file`"
          :icon="'fa-solid fa-file-circle-plus'" :class="{ 'reload-button': true, 'resume-color': resumeGame }"
          @click="openSelector()" :disabled="finishGame || peekAnswer" />

        <input type="file" id="select-file" accept=".tsv" ref="load_button" @change="readFile" value="CHOOSE FILE"
          class="select-file" />

        <fvl-button id="peek-answer-button" reference="peak-answer-button" :tooltip="`Peek Answer`"
          v-if="getSelfReadSwitch" :icon="'fa-solid fa-eye'" :class="{ 'kill-button': true }"
          @click="peekAnswerHandler()" :disabled="questionDisplayMode !== 'Question' && !peekAnswer" />

        <fvl-button id="collapse-container-button" reference="collapse-container-button" :tooltip="`Collapse Buttons`"
          :icon="'fa-solid fa-chevron-up'" :class="{ 'kill-button': true }" @click="collapseButtonContainer()" />
      </div>
    </div>
    <div class="notes-container" v-if="finishGame">
      <p class="notes-heading">GAME NOTES:</p>
      <textarea class="game-notes" maxlength="400" v-model="gameNotes" />
    </div>
    <div>
      <wait-spinner v-if="isSaving" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";
import WaitSpinner from "./WaitSpinner.vue";
import fvlButton from "./library/fvlButton.vue";

export default {
  name: "ButtonContainer",
  components: {
    WaitSpinner,
    fvlButton,
  },
  data() {
    return {
      stakeValue: 0,
      isSaving: false,
      inputText: "",
      gameNotes: "",
    };
  },
  computed: {
    ...mapState([
      "questionDisplayMode",
      "finishGame",
      "gameType",
      "gameId",
      "keyMap",
      "resumeGame",
      "gamePlayHistory",
      "peekAnswer"
    ]),
    ...mapGetters(["getPostMessageBody", "getSelfReadSwitch", "allPlayersEmpty"]),
    showQuestionLabel() {
      if (this.questionDisplayMode === "Start") return "Start Game";
      else if (this.finishGame) {
        return "Finish Game";
      } else {
        return "Show Question";
      }
    },
    isStakes() {
      return this.gameType === "stakes";
    },
  },
  methods: {
    ...mapActions([
      "passHandlerAction",
      "setFileName",
      "reloadQuestionData",
      "killHandlerAction",
      "correctHandlerAction",
      "wrongHandlerAction",
      "switchQuestionMode",
      "updateGameLog",
      "showNextQuestion",
      "updateGameResult",
      "showAnswerHandlerAction",
      "undoHandlerAction",
      "setGameNotes",
      "togglePeekAnswer",
      "setButtonContainerCompactFlag"
    ]),
    hotkeyActionHandler(button) {
      this.$refs[button].click();
    },
    commonButtonActions() {
      this.$parent.$refs.DisplayQuestion.clearImageExpand();
    },
    stakeHandler(x) {
      this.commonButtonActions();
      this.stakeValue = this.stakeValue === x ? 0 : x;
    },
    passHandler() {
      this.commonButtonActions();
      this.stakeValue = 0;
      this.passHandlerAction();
    },
    killHandler() {
      this.commonButtonActions();
      this.killHandlerAction();
    },
    correctHandler() {
      this.commonButtonActions();
      this.correctHandlerAction({ stakeValue: this.stakeValue });
      this.stakeValue = 0;
    },
    wrongHandler() {
      this.commonButtonActions();
      this.wrongHandlerAction({ stakeValue: this.stakeValue });
      this.stakeValue = 0;
    },
    undoHandler() {
      this.commonButtonActions();
      this.undoHandlerAction();
    },
    async showNextQuestionHandler() {
      if (this.questionDisplayMode === "Start") {
        const randomiserPlayerCount = this.$parent.$refs.RandomisePlayers.playerCount;
        if (randomiserPlayerCount > 0 && this.allPlayersEmpty) {
          const response = confirm("Are you sure you would like to start the game without randomising?")
          if (response) {
            this.switchQuestionMode("Answer");
          }
        } else {
          this.switchQuestionMode("Answer");
        }
      } else if (this.finishGame) {
        await this.setGameNotes(this.gameNotes);
        this.isSaving = true;
        await this.updateGameResult();
        await this.updateGameLog();
        setTimeout(() => {
          this.$router.push(`/result/${this.gameType}/${this.gameId}`);
        }, 100)
      } else {
        this.showNextQuestion();
      }
    },
    peekAnswerHandler() {
      if (this.peekAnswer) {
        this.switchQuestionMode("Question")
      } else {
        this.switchQuestionMode("Answer")
      }
      this.togglePeekAnswer()
    },
    openSelector() {
      this.$refs.load_button.click();
    },
    readFile(e) {
      e.target.files[0].text().then((t) => {
        this.inputText = t;
      });
      setTimeout(() => {
        this.reloadQuestions();
      }, 100);
    },

    collapseButtonContainer() {
      window.localStorage.setItem("compactButtons", true)
      this.setButtonContainerCompactFlag(true)
    },

    reloadQuestions() {
      let data = this.inputText.split("\n");
      const headers = data[0].split("\t").map((x) => x.trim());
      data = data.slice(1).map((x) => {
        const row = x.split("\t");

        const result = headers.reduce((prev, curr, index) => {
          if (curr && row.length === headers.length) {
            prev = { ...prev, [curr]: row[index] };
            if (curr === "roundNo") {
              const search = row[index].match(/Round ([0-9]+) Player ([0-9]+)/);
              if (search) {
                prev.round = parseInt(search[1]);
                prev.player = parseInt(search[2]);
              }
            }
            if (curr === "questionNo") {
              const search = row[index].match(/Question ([0-9]+)/);
              if (search) {
                prev.direct = parseInt(search[1]);
              }
            }
            if (curr === "imageUrl") {
              if (!row[index].match(/jpg|jpeg|png|static|wiki|gif|svg/i)) {
                prev.imageUrl = "";
              }
            }
          }
          return prev;
        }, {});
        return result;
      });

      const questions = data.filter((x) => x.round && x.player && x.direct);

      if (questions.length > 0) {
        this.reloadQuestionData(questions).then((res) => {
          if (res.success) {
            this.$toast.open({
              message: "Questions loaded",
              duration: 3000,
              type: "success",
            });
          } else {
            this.$toast.open({
              message: res.message,
              duration: 3000,
              type: "error",
            });
          }
        });
      } else {
        this.$toast.open({
          message: "Question List length = 0",
          duration: 3000,
          type: "error",
        });
      }
    },
  },
};
</script>

<style scoped>
.button-container {
  display: flex;
  margin: 20px 320px 5px;
  justify-content: center;
}

.show-question-button-container,
.scorer-button-container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.scorer-button-container {
  margin: 0 10px;
}

.show-question-answer {
  display: block;
  padding: 10px 15px;
  height: 80px;
  width: 260px;
}

.kill-undo-container {
  display: flex;
  height: 48px;
}

.finish-game {
  background-color: lightblue;
  font-weight: bold;
}

.c1.mimir-button {
  width: 441px;
}

.c1.mimir-button,
.c3.mimir-button {
  height: 78px;
}

.c3.mimir-button button {
  font-size: 42px;
}

.c2 {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 5px;
}

.c3 {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 5px;
}

.select-file {
  display: none;
}

.button-6.resume-color {
  background-color: #dbe186;
}

.notes-heading {
  margin: 0;
  font-weight: bold;
}

.notes-container {
  width: 100%;
  margin: 0 15px;
  display: flex;
  flex-direction: column;
}

.game-notes {
  font-size: 16px;
  width: 60%;
  height: 100px;
  resize: none;
  margin: auto;
}
</style>
